import { Modal } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ReactNode } from "react";

import { useStackedModalZIndex } from "~/hooks/useStackedModalZIndex";

// MUI Dialog maxWidth keys → px caps (values from the MUI default theme,
// which the legacy dialogs rendered with).
const MAX_WIDTHS: Record<string, number> = {
  xs: 444,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

export type DialogProps = {
  open: boolean;
  onClose?: (event?: unknown, reason?: string) => void;
  fullScreen?: boolean;
  fullWidth?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  isTransparent?: boolean;
  keepMounted?: boolean;
  /** Align the dialog to the top of the viewport instead of centering. */
  alignTop?: boolean;
  /**
   * Pin an explicit z-index. Omit for the default behaviour: the dialog joins
   * the shared modal stack so the most recently opened dialog always renders on
   * top (see {@link useStackedModalZIndex}). Only set this to opt out of that.
   */
  zIndex?: number;
  children?: ReactNode;
};

// Mantine Modal with the legacy MUI Dialog call-site API (`open`, `maxWidth`,
// `fullWidth`). Backdrop clicks never close (matches the old wrapper);
// escape still does.
const Dialog = ({
  open,
  onClose,
  fullScreen,
  fullWidth,
  maxWidth,
  isTransparent,
  keepMounted,
  alignTop,
  zIndex,
  children,
}: DialogProps) => {
  // Default to a stack-managed z-index (last-opened on top); an explicit
  // `zIndex` prop opts out.
  const stackedZIndex = useStackedModalZIndex(open);
  const resolvedZIndex = zIndex ?? stackedZIndex;

  const isSmallScreen = useMediaQuery("(max-width: 37.5em)", false, {
    getInitialValueInEffect: false,
  });

  // fullScreen can be forced by the prop or, when unset, by a small screen.
  const isFullScreen = fullScreen !== undefined ? fullScreen : isSmallScreen;

  // MUI dialogs kept a 32px margin on every side; mirror it so dialog
  // dimensions stay identical.
  const size = fullWidth
    ? "calc(100vw - 4rem)"
    : maxWidth
      ? MAX_WIDTHS[maxWidth]
      : "auto";

  return (
    <Modal
      opened={open}
      onClose={() => onClose?.(undefined, "closeButtonClick")}
      withCloseButton={false}
      closeOnClickOutside={false}
      centered={!alignTop}
      padding={0}
      size={size}
      yOffset="2rem"
      xOffset="2rem"
      // no scale transition: Highcharts measures its container on mount, and
      // a mid-transition transform breaks axis-label layout in chart dialogs
      transitionProps={{ duration: 0 }}
      keepMounted={keepMounted}
      fullScreen={isFullScreen}
      zIndex={resolvedZIndex}
      styles={{
        content: {
          backgroundColor: isTransparent
            ? "transparent"
            : "var(--mantine-color-bg-primary)",
          boxShadow: isTransparent ? "none" : undefined,
          // The MUI-margin sizing below emulates the old dialog (viewport minus
          // 32px margins, content-hugging width). A fullScreen dialog must fill
          // the viewport, so skip it and let Mantine's fullScreen sizing win —
          // otherwise the maxHeight cap leaves it short of full height.
          ...(isFullScreen
            ? {}
            : {
                // MUI paper hugged its content; without this, 100%-width
                // children (charts) stretch an auto-sized modal to the viewport
                width: size === "auto" ? "fit-content" : undefined,
                maxWidth: "100%",
                // MUI paper capped at viewport minus margins, scrolled inside
                maxHeight: "calc(100dvh - 4rem)",
                overflowY: "auto",
                marginInline: "auto",
              }),
        },
      }}
    >
      {children}
    </Modal>
  );
};

export default Dialog;
